/**
 * Narrowly scoped duplicate-submission testing exception.
 *
 * EXACTLY ONE account may re-submit an invoice that duplicate detection has
 * already flagged, and only after explicitly confirming the warning.
 *
 * This is deliberately NOT a role-based or admin bypass: staff and admins do
 * NOT inherit it. The allowlist lives here, once, and is consumed by both the
 * UI (to reveal the confirm action) and the server (to authorize the request).
 * The client copy only *reveals* a button — the authoritative check runs
 * server-side against the verified session (see duplicate-bypass.functions.ts).
 */

export const DUPLICATE_SUBMISSION_TEST_EMAILS: readonly string[] = [
  "thomasctovar@hotmail.com",
];

/** Trim + lowercase. Returns null for anything that isn't a usable string. */
export function normalizeEmail(email: unknown): string | null {
  if (typeof email !== "string") return null;
  const normalized = email.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
}

/** Exact (normalized) allowlist membership. No partial or domain matching. */
export function isDuplicateBypassEmail(email: unknown): boolean {
  const normalized = normalizeEmail(email);
  if (!normalized) return false;
  return DUPLICATE_SUBMISSION_TEST_EMAILS.some(
    (allowed) => allowed.trim().toLowerCase() === normalized,
  );
}

/**
 * Centralized eligibility rule.
 * True only when the user is authenticated AND their normalized email is
 * exactly an allowlisted testing address.
 */
export function canBypassDuplicateInvoiceWarning(
  user: { id?: string | null; email?: string | null } | null | undefined,
): boolean {
  if (!user?.id) return false;
  return isDuplicateBypassEmail(user.email);
}

/**
 * Server-side decision from verified session claims. Only the `email` claim
 * from the validated JWT is consulted — never client-supplied payload fields.
 */
export function isDuplicateBypassAuthorizedClaims(
  claims: Record<string, unknown> | null | undefined,
): boolean {
  if (!claims || typeof claims.sub !== "string" || !claims.sub) return false;
  return isDuplicateBypassEmail(claims.email);
}

export interface DuplicateWarningView {
  title: string;
  message: string;
  testingNote: string | null;
  actions: string[];
}

/**
 * Presentation contract for the duplicate warning. Detection is always on;
 * only the extra confirm action is gated by the allowlist.
 */
export function buildDuplicateWarningView(
  user: { id?: string | null; email?: string | null } | null | undefined,
): DuplicateWarningView {
  return duplicateWarningViewFor(canBypassDuplicateInvoiceWarning(user));
}

/** Same contract, from an already-resolved eligibility decision. */
export function duplicateWarningViewFor(canBypass: boolean): DuplicateWarningView {
  return {
    title: "Possible Duplicate Invoice",
    message: "This invoice appears to match a previous submission.",
    testingNote: canBypass
      ? "You are signed in with an authorized testing account. Submit Again for Testing creates a separate review and file number and lets you continue past unresolved readiness warnings. Submit As Live Invoice behaves exactly like a real customer submission — the readiness gate still stops you until every requirement is resolved."
      : null,
    actions: canBypass
      ? [
          "View Existing Review",
          "Cancel",
          "Submit Again for Testing",
          "Submit As Live Invoice",
        ]
      : ["View Existing Review", "Cancel"],
  };
}

